import React from 'react'
import { ResponsiveDialog } from './ResponsiveDialog'
import styles from '../containers/launchPage.module.scss'

export type LaunchFeatureDialogKey = 'customization' | 'signatures'

type LaunchFeatureDialogProps = {
  activeDialog: LaunchFeatureDialogKey | undefined
  onClose: () => void
}

export const LaunchFeatureDialog: React.FC<LaunchFeatureDialogProps> = ({
  activeDialog,
  onClose,
}) => {
  const isSignaturesDialog = activeDialog === 'signatures'

  return (
    <ResponsiveDialog
      isOpen={activeDialog !== undefined}
      onClose={onClose}
      ariaLabel={
        isSignaturesDialog
          ? 'Personal signature details'
          : 'Customization details'
      }
    >
      <div className={styles.featureDialogContent}>
        <div className={styles.featureDialogHeader}>
          <p className={styles.eyebrow}>
            {isSignaturesDialog ? 'Personal signatures' : 'Customization'}
          </p>
          <h2>
            {isSignaturesDialog
              ? 'Add your real signature to every card you send.'
              : 'Take customization to the next level.'}
          </h2>
          <p>
            {isSignaturesDialog
              ? 'Upload your handwritten signature once, then place it wherever your message needs a personal finishing touch.'
              : 'Create cards that feel unmistakably personal, from the first impression to the final signature.'}
          </p>
        </div>

        <div className={styles.featureDialogGrid}>
          <div className={styles.featureDialogCopy}>
            <h3>
              {isSignaturesDialog
                ? 'A personal touch that feels hand-finished.'
                : 'Creative card editor and customization.'}
            </h3>
            <p>
              {isSignaturesDialog
                ? 'Turn your real handwriting into a reusable card detail. Add your signature to birthday notes, thank-you cards, sympathy messages, customer follow-ups, and every heartfelt send that deserves to feel unmistakably from you.'
                : 'Personalize the front, inside, and back of every card with photos, layouts, colors, handwriting, signatures, and custom branding that match the message you want to send.'}
            </p>

            <div className={styles.featureDialogCards}>
              <article className={styles.featureDialogCard}>
                <span className={styles.featureDialogIcon}>Aa</span>
                <h4>
                  {isSignaturesDialog
                    ? 'Upload once, use anytime'
                    : 'Personal digital signature and handwriting font'}
                </h4>
                <p>
                  {isSignaturesDialog
                    ? 'Save your handwritten signature and add it to future cards without recreating it every time.'
                    : 'Add your own unique touch with handwriting and signatures that make each card feel hand-finished.'}
                </p>
              </article>

              <article className={styles.featureDialogCard}>
                <span className={styles.featureDialogIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path d="m12 19 7-7 3 3-7 7-3-3z" />
                    <path d="m18 13-1.5-7.5L2 2l3.5 14.5L13 18" />
                    <path d="m2 2 7.586 7.586" />
                    <circle cx="11" cy="11" r="2" />
                  </svg>
                </span>
                <h4>
                  {isSignaturesDialog
                    ? 'Place it with precision'
                    : 'Customize every detail, cover to cover'}
                </h4>
                <p>
                  {isSignaturesDialog
                    ? 'Position your signature naturally inside the card so your message feels personal, polished, and complete.'
                    : 'Curate a custom keepsake with favorite photos, thoughtful layouts, and polished design tools.'}
                </p>
              </article>
            </div>
          </div>

          <div className={styles.featureDialogMedia}>
            <div className={styles.featureDialogImageFrame}>
              <img
                src={
                  isSignaturesDialog
                    ? 'https://soc-website-public.s3.us-west-2.amazonaws.com/static/soc-website/images/manual/studio-display-signatures.png'
                    : 'https://soc-website-public.s3.us-west-2.amazonaws.com/static/soc-website/images/manual/soc-web-assets-kt.212.png'
                }
                alt={
                  isSignaturesDialog
                    ? 'Promptings signature editor displayed on desktop'
                    : 'Promptings card catalog on desktop'
                }
              />
            </div>
          </div>
        </div>
      </div>
    </ResponsiveDialog>
  )
}
